import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

export function useDashboard(entries) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMood, setFilterMood] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Pagination state
  const [loadedEntries, setLoadedEntries] = useState([]);
  const [, setLastEvaluatedKey] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalEntries, setTotalEntries] = useState(0);
  
  // Track if search/filter is active
  const isFiltered = Boolean(searchQuery.trim() || filterMood !== 'all');
  
  // Use ref to track lastEvaluatedKey to avoid dependency issues
  const lastEvaluatedKeyRef = useRef(null);
  const initialEntriesLoadedRef = useRef(false);
  
  // SessionStorage keys for persistence
  const STORAGE_KEYS = {
    loadedEntries: 'dashboard_loaded_entries',
    lastEvaluatedKey: 'dashboard_last_evaluated_key',
    hasMore: 'dashboard_has_more'
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load stats from stats API
  const [statsData, setStatsData] = useState(null);
  
  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await api.entries.getStats();
        // API service returns response.data which contains { stats: { totalEntries, ... } }
        // So response.stats contains the stats object
        if (response && response.stats) {
          setStatsData(response.stats);
          setTotalEntries(response.stats.totalEntries || 0);
        }
      } catch (error) {
        console.error('Error loading stats:', error);
        // Fallback to entries length if stats API fails
        setTotalEntries(entries.length);
      }
    };
    
    if (api.isAuthenticated()) {
      loadStats();
    }
  }, [entries.length]);

  // Restore persisted state from sessionStorage on mount
  useEffect(() => {
    if (!isFiltered) {
      try {
        const savedEntries = sessionStorage.getItem(STORAGE_KEYS.loadedEntries);
        const savedLastKey = sessionStorage.getItem(STORAGE_KEYS.lastEvaluatedKey);
        const savedHasMore = sessionStorage.getItem(STORAGE_KEYS.hasMore);
        
        if (savedEntries) {
          const parsedEntries = JSON.parse(savedEntries);
          if (Array.isArray(parsedEntries) && parsedEntries.length > 0) {
            setLoadedEntries(parsedEntries);
            initialEntriesLoadedRef.current = true;
            
            if (savedLastKey) {
              const parsedKey = JSON.parse(savedLastKey);
              lastEvaluatedKeyRef.current = parsedKey;
              setLastEvaluatedKey(parsedKey);
            }
            
            if (savedHasMore !== null) {
              setHasMore(savedHasMore === 'true');
            }
            
            return; // Don't proceed with initial load if we restored from storage
          }
        }
      } catch (error) {
        console.error('Error restoring from sessionStorage:', error);
        // Clear corrupted data
        sessionStorage.removeItem(STORAGE_KEYS.loadedEntries);
        sessionStorage.removeItem(STORAGE_KEYS.lastEvaluatedKey);
        sessionStorage.removeItem(STORAGE_KEYS.hasMore);
      }
    }
    
    // Initialize: Use entries prop as initial load (first 50 from App.js)
    if (!initialEntriesLoadedRef.current && entries.length > 0) {
      setLoadedEntries(entries);
      initialEntriesLoadedRef.current = true;
      
      // Fetch pagination key by making an API call (we'll use the same call that App.js made)
      // This ensures we have lastEvaluatedKey for loading more entries
      const fetchPaginationKey = async () => {
        try {
          const data = await api.entries.getEntries({ 
            limit: 50, 
            sort: '-timestamp' 
          });
          lastEvaluatedKeyRef.current = data.pagination?.lastEvaluatedKey || null;
          setLastEvaluatedKey(data.pagination?.lastEvaluatedKey || null);
          setHasMore(data.pagination?.hasMore || false);
          
          // Save to sessionStorage
          if (!isFiltered) {
            sessionStorage.setItem(STORAGE_KEYS.loadedEntries, JSON.stringify(entries));
            if (data.pagination?.lastEvaluatedKey) {
              sessionStorage.setItem(STORAGE_KEYS.lastEvaluatedKey, JSON.stringify(data.pagination.lastEvaluatedKey));
            }
            sessionStorage.setItem(STORAGE_KEYS.hasMore, String(data.pagination?.hasMore || false));
          }
        } catch (error) {
          console.error('Error fetching pagination key:', error);
          // Fallback: check against totalEntries
          if (totalEntries > 0) {
            setHasMore(entries.length < totalEntries);
          } else {
            setHasMore(entries.length >= 50);
          }
        }
      };
      
      fetchPaginationKey();
    }
  }, [entries, totalEntries, isFiltered]);

  // Fetch entries from API (with pagination)
  const fetchEntries = useCallback(async (reset = true) => {
    if (reset) {
      setLoading(true);
      setLoadedEntries([]);
      lastEvaluatedKeyRef.current = null;
      setLastEvaluatedKey(null);
      setHasMore(true);
      initialEntriesLoadedRef.current = false;
    } else {
      setLoadingMore(true);
    }

    try {
      const filters = {
        sort: '-timestamp',
        limit: 50, // Load 50 entries at a time
      };

      if (searchQuery.trim()) {
        filters.search = searchQuery.trim();
      }

      if (filterMood !== 'all') {
        filters.mood = filterMood;
      }

      // For loading more (not reset), use lastEvaluatedKey if available
      // If we don't have lastEvaluatedKey but have loaded entries, we need to get it from API
      if (!reset && lastEvaluatedKeyRef.current) {
        filters.lastEvaluatedKey = lastEvaluatedKeyRef.current;
      }

      const data = await api.entries.getEntries(filters);
      const formattedEntries = api.formatEntriesForDisplay(data.entries);

      if (reset) {
        setLoadedEntries(formattedEntries);
      } else {
        // Filter out duplicates using functional update
        setLoadedEntries(prev => {
          const existingIds = new Set(prev.map(e => e.id || e.entryId));
          const newEntries = formattedEntries.filter(e => !existingIds.has(e.id || e.entryId));
          return [...prev, ...newEntries];
        });
      }

      // Calculate final entries list
      const finalEntries = reset ? formattedEntries : (() => {
        const existingIds = new Set(loadedEntries.map(le => le.id || le.entryId));
        const newEntries = formattedEntries.filter(e => !existingIds.has(e.id || e.entryId));
        return [...loadedEntries, ...newEntries];
      })();
      
      // Update pagination state
      lastEvaluatedKeyRef.current = data.pagination?.lastEvaluatedKey || null;
      setLastEvaluatedKey(data.pagination?.lastEvaluatedKey || null);
      
      // Determine hasMore
      let finalHasMore = data.pagination?.hasMore || false;
      if (!data.pagination?.hasMore && totalEntries > 0) {
        finalHasMore = finalEntries.length < totalEntries;
      }
      setHasMore(finalHasMore);
      
      // Persist to sessionStorage (only when not filtered)
      if (!isFiltered) {
        try {
          sessionStorage.setItem(STORAGE_KEYS.loadedEntries, JSON.stringify(finalEntries));
          if (data.pagination?.lastEvaluatedKey) {
            sessionStorage.setItem(STORAGE_KEYS.lastEvaluatedKey, JSON.stringify(data.pagination.lastEvaluatedKey));
          } else {
            sessionStorage.removeItem(STORAGE_KEYS.lastEvaluatedKey);
          }
          sessionStorage.setItem(STORAGE_KEYS.hasMore, String(finalHasMore));
        } catch (error) {
          console.error('Error saving to sessionStorage:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
      if (reset) {
        // On error, fallback to entries from props
        setLoadedEntries(entries);
        setHasMore(entries.length >= 50);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchQuery, filterMood, entries, isFiltered, totalEntries]);

  // Load more entries (for Load More button) - works for both filtered and unfiltered
  const loadMoreEntries = useCallback(async () => {
    if (hasMore && !loadingMore && !loading) {
      await fetchEntries(false);
    }
  }, [hasMore, loadingMore, loading, fetchEntries]);

  // When search/filter changes, fetch from API
  useEffect(() => {
    if (isFiltered) {
      // Clear persisted state when filtering/searching
      sessionStorage.removeItem(STORAGE_KEYS.loadedEntries);
      sessionStorage.removeItem(STORAGE_KEYS.lastEvaluatedKey);
      sessionStorage.removeItem(STORAGE_KEYS.hasMore);
      
      const timeoutId = setTimeout(() => {
        fetchEntries(true);
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      // Reset to initial entries when clearing search/filter
      if (initialEntriesLoadedRef.current) {
        // Try to restore from sessionStorage first
        try {
          const savedEntries = sessionStorage.getItem(STORAGE_KEYS.loadedEntries);
          if (savedEntries) {
            const parsedEntries = JSON.parse(savedEntries);
            if (Array.isArray(parsedEntries) && parsedEntries.length > 0) {
              setLoadedEntries(parsedEntries);
              
              const savedLastKey = sessionStorage.getItem(STORAGE_KEYS.lastEvaluatedKey);
              if (savedLastKey) {
                const parsedKey = JSON.parse(savedLastKey);
                lastEvaluatedKeyRef.current = parsedKey;
                setLastEvaluatedKey(parsedKey);
              }
              
              const savedHasMore = sessionStorage.getItem(STORAGE_KEYS.hasMore);
              if (savedHasMore !== null) {
                setHasMore(savedHasMore === 'true');
              }
              
              return; // Don't reset if we restored from storage
            }
          }
        } catch (error) {
          console.error('Error restoring from sessionStorage:', error);
        }
        
        // Fallback to initial entries if no saved state
        setLoadedEntries(entries);
        lastEvaluatedKeyRef.current = null;
        setLastEvaluatedKey(null);
        // Update hasMore based on totalEntries
        if (totalEntries > 0) {
          setHasMore(entries.length < totalEntries);
        } else {
          setHasMore(entries.length >= 50);
        }
      }
    }
  }, [searchQuery, filterMood, isFiltered, fetchEntries, entries, totalEntries]);

  // Determine which entries to display
  // Use loadedEntries if we have them (from API), otherwise use entries from props
  const filteredEntries = (isFiltered || loadedEntries.length > entries.length) 
    ? loadedEntries 
    : entries;
  
  // Update hasMore based on total count when not filtered
  useEffect(() => {
    if (!isFiltered && totalEntries > 0) {
      setHasMore(filteredEntries.length < totalEntries);
    }
  }, [isFiltered, totalEntries, filteredEntries.length]);

  // Calculate stats - use API stats when available, otherwise calculate from entries
  const stats = {
    totalEntries: statsData?.totalEntries || totalEntries || filteredEntries.length,
    thisWeek: statsData?.entriesThisWeek ?? filteredEntries.filter(e => {
      const entryDate = new Date(e.timestamp || Date.now());
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      return entryDate >= weekAgo;
    }).length,
    topMood: statsData?.mostFrequentMood || (() => {
      const moodCounts = filteredEntries.reduce((acc, entry) => {
        acc[entry.mood] = (acc[entry.mood] || 0) + 1;
        return acc;
      }, {});
      return Object.keys(moodCounts).sort((a, b) => moodCounts[b] - moodCounts[a])[0] || null;
    })()
  };

  // Function to remove entry from sessionStorage when deleted
  const removeEntryFromStorage = useCallback((entryId) => {
    if (!isFiltered) {
      try {
        const savedEntries = sessionStorage.getItem(STORAGE_KEYS.loadedEntries);
        if (savedEntries) {
          const parsedEntries = JSON.parse(savedEntries);
          const filtered = parsedEntries.filter(e => (e.id || e.entryId) !== entryId);
          sessionStorage.setItem(STORAGE_KEYS.loadedEntries, JSON.stringify(filtered));
        }
      } catch (error) {
        console.error('Error updating sessionStorage after delete:', error);
      }
    }
  }, [isFiltered]);

  return {
    searchQuery,
    setSearchQuery,
    filterMood,
    setFilterMood,
    deleteConfirm,
    setDeleteConfirm,
    filterOpen,
    setFilterOpen,
    isMobile,
    loading,
    loadingMore,
    filteredEntries,
    stats,
    hasMore,
    loadMoreEntries,
    isFiltered,
    removeEntryFromStorage
  };
}
